/**
 * PeopleSoft term (STRM) helpers.
 *
 * The enrollment feed's SIS IDs carry a human term prefix as their first
 * whitespace-delimited token — `"SU26 A-ELE   75D 1MIN"` → `SU26`. The
 * authoritative 4-digit PeopleSoft STRM for the same term appears only in
 * the SIS COURSES file (`term_id`), following the standard pattern:
 *
 *   STRM = "2" + <2-digit year> + <season digit>
 *
 * Confirmed against real drops: SP26 → 2263, SU26 → 2265. Fall follows the
 * same scheme with digit 7. The worker uses this mapping to stamp each
 * run's detected term(s) so the dashboard can show the 4-digit term the
 * feed "came over" with and validate it against the operator-entered
 * expected terms (see `AppSettings.expectedPrimaryTerm` /
 * `expectedSecondaryTerm`).
 */

/** Season prefix → STRM season digit. */
export const TERM_SEASON_DIGITS: Record<string, string> = {
  SP: "3",
  SU: "5",
  FA: "7",
};

/**
 * `"SU26 A-ELE   75D 1MIN"` → `"SU26"`. Returns undefined for IDs that
 * don't start with a recognizable `<season><2-digit-year>` token, so noise
 * rows can't pollute the detected-term set.
 */
export function termPrefixFromSisId(sisId: string): string | undefined {
  const token = sisId.trimStart().split(/\s/, 1)[0];
  return token && /^[A-Z]{2}\d{2}$/.test(token) ? token : undefined;
}

/**
 * `"SU26"` → `"2265"`. Returns undefined for prefixes whose season isn't in
 * the mapping (callers fall back to showing the raw prefix, which will also
 * fail expected-term validation — the safe direction for an unknown feed).
 */
export function termPrefixToStrm(prefix: string): string | undefined {
  const m = /^([A-Z]{2})(\d{2})$/.exec(prefix);
  if (!m || !m[1] || !m[2]) return undefined;
  const digit = TERM_SEASON_DIGITS[m[1]];
  return digit ? `2${m[2]}${digit}` : undefined;
}

/** STRM season digit → season prefix (inverse of TERM_SEASON_DIGITS). */
const TERM_SEASON_PREFIXES: Record<string, string> = Object.fromEntries(
  Object.entries(TERM_SEASON_DIGITS).map(([prefix, digit]) => [digit, prefix]),
);

/**
 * `"2265"` → `"SU26"` — inverse of `termPrefixToStrm`. Returns undefined
 * for values that don't decode (wrong shape or a season digit outside
 * 3/5/7), so callers fall back to the raw value.
 */
export function strmToTermPrefix(strm: string): string | undefined {
  const m = /^2(\d{2})(\d)$/.exec(strm);
  if (!m || !m[1] || !m[2]) return undefined;
  const prefix = TERM_SEASON_PREFIXES[m[2]];
  return prefix ? `${prefix}${m[1]}` : undefined;
}

/**
 * Operator-facing label for a stored term value. Canvas and the feed use
 * the short description (`FA26`), so that leads; the authoritative
 * PeopleSoft STRM stays in parens for tickets/PeopleSoft lookups:
 * `"2267"` → `"FA26 (2267)"`. Values that don't decode (raw prefixes from
 * an unrecognized season, noise) render unchanged.
 */
export function formatTermLabel(term: string): string {
  const prefix = strmToTermPrefix(term);
  return prefix ? `${prefix} (${term})` : term;
}

/** True for a plausible 4-digit STRM ("2265"). Used to validate operator input. */
export function isValidStrm(value: string): boolean {
  return /^\d{4}$/.test(value);
}

/**
 * Map a set of term prefixes to the sorted, distinct term list a run row
 * stores: each prefix becomes its 4-digit STRM, or stays raw when the
 * season is unrecognized (visible + fails validation rather than hidden).
 */
export function detectedTermsFromPrefixes(
  prefixes: Iterable<string>,
): string[] {
  const terms = new Set<string>();
  for (const prefix of prefixes) {
    terms.add(termPrefixToStrm(prefix) ?? prefix);
  }
  return [...terms].sort();
}
